import {useMediaQuery, useTheme} from '@mui/material';
import classNames from 'classnames';

import {AddToCartNormalIcon} from 'assets/icons/addToCartNormal';
import {FloatingQuantityPickerWrapper} from 'components/floating-quantity-picker/FloatingQuantityPickerWrapper';
import {StyledFloatingQuantityPickerContainer} from 'components/floating-quantity-picker/styles';
import {config} from 'config/env';
import {isPDPRevamp} from 'constants/recommendation';
import {useCounter} from 'hooks/recommendation-hooks/useCounter';
import {useFloatingQuantityPicker} from 'hooks/recommendation-hooks/useFloatingQuantityPicker';
import {useCurrentBasketItem} from 'hooks/useCurrentBasketItem';
import {useProductPriceAndText} from 'hooks/useProductPriceAndText';
import {Product} from 'types';
import {getCurrencySymbol} from 'utils';

import {
  NoDiscountNormalPriceWrapper,
  StyledNoDiscountAddToBasketBtn,
  StyledNoDiscountCurrentPriceSymbolTypography,
  StyledNoDiscountCurrentPriceTypography,
  StyledNoDiscountNormalPrice,
  StyledNoDiscountPriceContainer,
  StyledUpdatedQuanityTypography,
  WeightedFreshKgText
} from './styles';

export type ProductCardLeoPriceBoxProps = {
  product: Product;
  productCount: number;
  isShowAddtoCartBtn: boolean;
  handleClickAddToBasket: (e: React.MouseEvent<HTMLDivElement>) => void;
  isPDP: boolean;
  isAddToCart: boolean;
  addAnalytics: () => void;
  sendCarouselImpressionAnalytics: () => void;
  isSearchBoxCarousel?: boolean;
  showOnlyAddToCartButton?: boolean;
};

const ProductCardLEOPriceBox: React.FC<ProductCardLeoPriceBoxProps> = ({
  product,
  productCount,
  isShowAddtoCartBtn,
  handleClickAddToBasket,
  isPDP,
  isAddToCart,
  addAnalytics,
  isSearchBoxCarousel,
  showOnlyAddToCartButton,
  sendCarouselImpressionAnalytics
}) => {
  const theme = useTheme();
  const {basketItem} = useCurrentBasketItem(product);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isFloatingPickerEnabled = config.RECOM_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER;

  const {productDisplayPrice, kgText, quantity} = useProductPriceAndText({product, productCount});
  const {togglePicker, showPicker, closePicker} = useFloatingQuantityPicker({
    id: `${product.id}_${product.makroId}`
  });
  const showQuantityPicker = showPicker && isFloatingPickerEnabled;

  const showKg = product?.isWeightedFresh;
  const enablePartialQuantity = showKg && product?.moq === 1 && product?.sellInPiece === false;
  const {count, inProgress, onAdd, onIncrement, onChange, onDecrement, onDelete} = useCounter({
    initialCount: basketItem?.quantity,
    enablePartialQuantity,
    product,
    closePicker,
    sendCarouselImpressionAnalytics
  });
  const iconSize = isMobileView || showOnlyAddToCartButton ? 28 : 32;

  const renderFloatingQuantityPicker = () =>
    showQuantityPicker && (
      <StyledFloatingQuantityPickerContainer>
        <FloatingQuantityPickerWrapper
          max={product.totalInventory}
          disabled={inProgress}
          onChange={onChange}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          showKg={showKg}
          moq={product?.moq}
          isPDP={false}
          initialValue={count}
          onDelete={onDelete}
          size={'medium'}
          enablePartialQuantity={enablePartialQuantity}
        />
      </StyledFloatingQuantityPickerContainer>
    );

  const StyledNoDiscountAddToBasketBtnAnchor = (
    <StyledNoDiscountAddToBasketBtn
      data-test-id={'btn_add_to_basket'}
      onClick={async (e) => {
        e.stopPropagation();
        if (isFloatingPickerEnabled) {
          if (productCount === 0 && onAdd) {
            await onAdd(e);
            togglePicker();
          } else {
            togglePicker();
          }
        } else {
          handleClickAddToBasket(e);
        }
        if (!isPDPRevamp) {
          addAnalytics();
        }
      }}
      className={classNames({
        updatedQuantityBgColor: quantity,
        normalQuantityBgColor: !quantity,
        compact: showOnlyAddToCartButton
      })}
    >
      {renderFloatingQuantityPicker()}
      {quantity ? (
        <StyledUpdatedQuanityTypography
          className={classNames({
            medium: quantity?.toString().length === 4,
            small: quantity?.toString().length === 5,
            extraSmall: quantity?.toString().length > 5
          })}
        >
          {quantity}
        </StyledUpdatedQuanityTypography>
      ) : (
        <AddToCartNormalIcon width={iconSize} height={iconSize} />
      )}
    </StyledNoDiscountAddToBasketBtn>
  );

  if (showOnlyAddToCartButton) {
    return StyledNoDiscountAddToBasketBtnAnchor;
  }

  return (
    <StyledNoDiscountPriceContainer
      data-test-id={'product_price'}
      className={classNames({
        pdp: isPDP,
        addToCart: isAddToCart
      })}
    >
      {!isSearchBoxCarousel && (
        <StyledNoDiscountNormalPrice className="price">
          <NoDiscountNormalPriceWrapper>
            <StyledNoDiscountCurrentPriceTypography
              className={classNames({
                addToCart: isAddToCart,
                pdp: isPDP
              })}
            >
              {`${productDisplayPrice?.numberValue}`}
            </StyledNoDiscountCurrentPriceTypography>
            <StyledNoDiscountCurrentPriceSymbolTypography>
              {getCurrencySymbol()}
              {kgText && (
                <WeightedFreshKgText
                  className={classNames({
                    pdp: isPDP,
                    addToCart: isAddToCart
                  })}
                >
                  {kgText}
                </WeightedFreshKgText>
              )}
            </StyledNoDiscountCurrentPriceSymbolTypography>
          </NoDiscountNormalPriceWrapper>
        </StyledNoDiscountNormalPrice>
      )}

      {isShowAddtoCartBtn && (
        <StyledNoDiscountNormalPrice
          className={classNames({
            isSearchBoxCarousel: isSearchBoxCarousel
          })}
        >
          {StyledNoDiscountAddToBasketBtnAnchor}
        </StyledNoDiscountNormalPrice>
      )}
    </StyledNoDiscountPriceContainer>
  );
};

export default ProductCardLEOPriceBox;
